/**
 * X Ads — line item rules the page enforces before anything reaches X: the backend's bidding
 * rule (checkBidding) and the targeting body replaceTargeting reads.
 */
import type { XTargetingInput } from '@/services/x-ads.service';

/** The backend's bidding rule, so the page never sends a combination X refuses. */
export function biddingProblem(objective: string, strategy: string, bidMicros: number | null): string | null {
  if (strategy === 'MAX' && (objective === 'REACH' || objective === 'FOLLOWERS')) return 'Maximum bid is not available for Reach or Followers — use Automatic or Target bid.';
  if (strategy === 'TARGET' && !['REACH', 'FOLLOWERS', 'WEBSITE_CLICKS'].includes(objective)) return 'Target bid is only available for Reach, Followers and Website clicks.';
  if ((strategy === 'MAX' || strategy === 'TARGET') && !(bidMicros && bidMicros > 0)) return 'Enter a bid amount for Maximum / Target bid.';
  return null;
}
export const strategyAllowed = (objective: string, strategy: string) => biddingProblem(objective, strategy, 1) === null;

export interface Opt { value: string; label: string; hint?: string }
export type MatchType = 'BROAD' | 'PHRASE' | 'EXACT' | 'UNORDERED';
export interface Keyword { text: string; matchType: MatchType; exclude: boolean }

export interface TargetingState {
  locations: Opt[];
  languages: Opt[];
  interests: Opt[];
  platforms: string[];
  gender: '' | '1' | '2';
  ageBucket: string;
  keywords: Keyword[];
  customAudiences: Opt[];
  excludedCustomAudiences: Opt[];
}

export const emptyTargeting = (): TargetingState => ({
  locations: [], languages: [], interests: [], platforms: [], gender: '', ageBucket: '', keywords: [], customAudiences: [], excludedCustomAudiences: [],
});

const values = (opts: Opt[]) => opts.map((o) => o.value);

/** The body of POST line-items `targeting` / PUT line-items/{id}/targeting; empty parts are left out. */
export function toTargetingInput(t: TargetingState): XTargetingInput {
  const out: XTargetingInput = {};
  if (t.locations.length) out.locations = values(t.locations);
  if (t.languages.length) out.languages = values(t.languages);
  if (t.interests.length) out.interests = values(t.interests);
  if (t.platforms.length) out.platforms = t.platforms;
  if (t.gender) out.gender = t.gender;
  if (t.ageBucket) out.ageBucket = t.ageBucket;
  if (t.keywords.length) out.keywords = t.keywords.map((k) => ({ text: k.text, matchType: k.matchType, exclude: k.exclude }));
  if (t.customAudiences.length) out.customAudiences = values(t.customAudiences);
  if (t.excludedCustomAudiences.length) out.excludedCustomAudiences = values(t.excludedCustomAudiences);
  return out;
}

export const targetingCount = (t: XTargetingInput): number =>
  (t.locations?.length ?? 0) + (t.languages?.length ?? 0) + (t.interests?.length ?? 0) + (t.platforms?.length ?? 0)
  + (t.gender ? 1 : 0) + (t.ageBucket ? 1 : 0) + (t.keywords?.length ?? 0) + (t.customAudiences?.length ?? 0) + (t.excludedCustomAudiences?.length ?? 0);
